import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { isEqual } from 'lodash';

import { UserColumnsSettingsService } from '../../services/user-columns-settings.service';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { ColumnsSettings } from '../../models';
import { TableColumnModel } from 'common/src/models/table-column.model';

@Component({
  selector: 'rnpl-columns-settings',
  templateUrl: './columns-settings.component.html',
  styleUrls: ['./columns-settings.component.scss'],
})
export class ColumnsSettingsComponent implements OnChanges, OnDestroy {

  public customizedColumns: { [key: string]: boolean };

  @Input() public documentStatus: string; // if document dont have statuses than pass document type
  @Input() public documentType: DocumentTypesUppercaseEnum|string;
  @Input() public sourceColumnsConfig: TableColumnModel[];
  @Input() public disabled = false;

  @Output() customized: EventEmitter<TableColumnModel[]> = new EventEmitter<TableColumnModel[]>();

  public refreshing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private valueChange$: Subject<{ prop: string, value: boolean }> = new Subject<{ prop: string, value: boolean }>();
  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private localChangeVersion = 0;

  constructor(private userColumnsSettingsService: UserColumnsSettingsService) {
    this.valueChange$
      .pipe(
        debounceTime(250),
        takeUntil(this.destroy$)
      )
      .subscribe((change: { prop: string, value: boolean }) => this.updateColumnsSettings(change));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.hasOwnProperty('documentType') && this.documentType) ||
      (changes.hasOwnProperty('sourceColumnsConfig') && this.documentType && this.documentStatus) ||
      (changes.hasOwnProperty('documentStatus') && this.documentStatus)
    ) {
      this.getColumnsSettings();
    }
  }

  public refreshColumnsSettings(): void {
    this.refreshing$.next(true);
    this.getColumnsSettings();
  }

  private getColumnsSettings(): void {
    this.loading$.next(true);
    this.userColumnsSettingsService.getColumnsSettings(this.documentStatus, this.documentType)
      .pipe(
        finalize(() => {
          this.refreshing$.next(false);
          this.loading$.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((columnsSettings: ColumnsSettings) => {
        this.customizedColumns = columnsSettings.columns;
        if (!columnsSettings.columns) {
          this.customized.emit(this.sourceColumnsConfig.filter((c: TableColumnModel) => !c.hiddenByDefault));
        } else {
          this.filterColumnsBySettings();
        }
      });
  }

  private updateColumnsSettings(change: { prop: string, value: boolean }): void {
    const updateCustomizedColumns: {[key: string]: boolean} = this.getColumnsState();
    updateCustomizedColumns[change.prop] = change.value;

    const columnsSettings: ColumnsSettings = {
      type: this.documentType,
      status: this.documentStatus,
      columns: updateCustomizedColumns
    };

    const savedVersion = this.localChangeVersion;
    this.userColumnsSettingsService.saveColumnsSettings(columnsSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((columnsSettings: ColumnsSettings) => {
        // a newer toggle is already shown and will be saved next
        if (savedVersion !== this.localChangeVersion) { return; }
        // nothing to re-render if the stored state is the one already shown
        if (isEqual(columnsSettings.columns, this.customizedColumns)) { return; }
        this.customizedColumns = columnsSettings.columns;
        this.filterColumnsBySettings();
      }, () => {
        // restore the saved state if the change was not stored
        this.getColumnsSettings();
      });
  }

  private getColumnsState(): {[key: string]: boolean} {
    const columnsState: {[key: string]: boolean} = {};
    this.sourceColumnsConfig.map((column: TableColumnModel) => {
      if (column.hasOwnProperty('customizable') && column.customizable) {
        columnsState[column.prop] = (this.customizedColumns && this.customizedColumns.hasOwnProperty(column.prop))
          ? !!this.customizedColumns[column.prop]
          : !column.hiddenByDefault;
      }
    });
    return columnsState;
  }

  public toggleColumn(value: boolean, prop: string): void {
    // apply the change right away, the request is sent in the background
    this.localChangeVersion++;
    this.customizedColumns = { ...this.getColumnsState(), [prop]: value };
    this.filterColumnsBySettings();
    this.valueChange$.next({value, prop});
  }

  private filterColumnsBySettings(): void {
    const filteredColumns: TableColumnModel[] = [...this.sourceColumnsConfig]
      .map(c => ({ ...c })) // clone
      .filter((c: TableColumnModel) => {
         if (c.hasOwnProperty('customizable')) {
           if (!this.customizedColumns.hasOwnProperty(c.prop) && !c.hiddenByDefault) {
             // always display new columns that missed in saved config, except hidden by default columns
             return true;
           }
           return this.customizedColumns[c.prop];
         }
         return true;
      });

    const notFixedColumns = filteredColumns.filter((c: TableColumnModel) => !c.maxWidth || c.maxWidth > 40);

    if (notFixedColumns && notFixedColumns.length && notFixedColumns.every(c => !!c.maxWidth)) {
        const lastIndex = filteredColumns
          .findIndex(c => c.prop === notFixedColumns[notFixedColumns.length - 1].prop);
        filteredColumns[lastIndex].maxWidth = null;
    }

    // const customizableFilteredColumns: TableColumnModel[] = filteredColumns
    //   .filter(c => c.hasOwnProperty('customizable') && c.customizable);
    //
    // // remove fixed width from last column if needed
    // if (customizableFilteredColumns.length && customizableFilteredColumns.every(c => !!c.maxWidth)) {
    //   const lastCustomizableIndex = filteredColumns
    //     .findIndex(c => c.prop === customizableFilteredColumns[customizableFilteredColumns.length - 1].prop);
    //   filteredColumns[lastCustomizableIndex].maxWidth = null;
    // }
    this.customized.emit(filteredColumns);
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
